import React, { useState, useEffect, useRef } from 'react';
import SearchBox from './SearchBox';
import { authFetch, authPost } from '../lib/authFetch';
import './StrategyView.css';

function StrategyView({ dataMode, notebooks: propNotebooks, activeNotebookId, onNotebooksUpdated }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notebooks, setNotebooks] = useState(propNotebooks || []);
  const [activeNotebook, setActiveNotebook] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);

  // Sync notebooks from props
  useEffect(() => {
    if (propNotebooks) {
      setNotebooks(propNotebooks);
    }
  }, [propNotebooks]);

  // Set active notebook when activeNotebookId changes
  useEffect(() => {
    if (activeNotebookId && notebooks.length > 0) {
      const notebook = notebooks.find(n => n.id === activeNotebookId);
      if (notebook) {
        setActiveNotebook(notebook);
      }
    }
  }, [activeNotebookId, notebooks]);

  // Load saved notebooks on mount (only if not provided via props)
  useEffect(() => {
    if (!propNotebooks) {
      fetchNotebooks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMode, propNotebooks]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeNotebook?.messages]);

  const fetchNotebooks = async () => {
    try {
      const response = await authFetch(`/api/notebooks?mode=${dataMode}`);
      if (response.ok) {
        const data = await response.json();
        const fetchedNotebooks = data.notebooks || [];
        setNotebooks(fetchedNotebooks);
        if (onNotebooksUpdated) {
          onNotebooksUpdated(fetchedNotebooks);
        }
      }
    } catch (error) {
      console.error('Error fetching notebooks:', error);
    }
  };

  // Helper to update notebooks and notify parent
  const updateNotebooks = (updater) => {
    setNotebooks(prev => {
      const updated = typeof updater === 'function' ? updater(prev) : updater;
      if (onNotebooksUpdated) {
        onNotebooksUpdated(updated);
      }
      return updated;
    });
  };

  const createNewNotebook = () => {
    const newNotebook = {
      id: Date.now().toString(),
      title: 'New Strategy',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    updateNotebooks(prev => [newNotebook, ...prev]);
    setActiveNotebook(newNotebook);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    const query = searchQuery;

    // If no active notebook, create one
    let notebook = activeNotebook;
    if (!notebook) {
      notebook = {
        id: Date.now().toString(),
        title: 'New Strategy',
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      updateNotebooks(prev => [notebook, ...prev]);
      setActiveNotebook(notebook);
    }

    // Add user message
    const userMessage = {
      role: 'user',
      content: query,
      timestamp: new Date().toISOString()
    };

    const updatedMessages = [...notebook.messages, userMessage];
    const updatedNotebook = { ...notebook, messages: updatedMessages, updatedAt: new Date().toISOString() };

    setActiveNotebook(updatedNotebook);
    updateNotebooks(prev => prev.map(n => n.id === notebook.id ? updatedNotebook : n));
    setSearchQuery('');
    setIsLoading(true);

    try {
      const response = await authPost('/api/strategy', {
        query: query,
        mode: dataMode,
        conversationHistory: updatedMessages,
        notebookId: notebook.id
      });

      const data = await response.json();

      if (response.ok && data.content) {
        // Add assistant message
        const assistantMessage = {
          role: 'assistant',
          content: data.content,
          timestamp: new Date().toISOString()
        };

        // Update title if this is the first exchange
        const newTitle = notebook.messages.length === 0 && data.title ? data.title : notebook.title;

        const finalNotebook = {
          ...updatedNotebook,
          title: newTitle,
          messages: [...updatedMessages, assistantMessage],
          updatedAt: new Date().toISOString()
        };

        setActiveNotebook(finalNotebook);
        updateNotebooks(prev => prev.map(n => n.id === notebook.id ? finalNotebook : n));

        // Save to backend
        await authPost('/api/notebooks/save', {
          notebook: finalNotebook, mode: dataMode
        });
      } else {
        // Handle error - add error message
        const errorMessage = {
          role: 'assistant',
          content: data.error || 'Sorry, something went wrong. Please try again.',
          timestamp: new Date().toISOString()
        };

        const errorNotebook = {
          ...updatedNotebook,
          messages: [...updatedMessages, errorMessage],
          updatedAt: new Date().toISOString()
        };

        setActiveNotebook(errorNotebook);
        updateNotebooks(prev => prev.map(n => n.id === notebook.id ? errorNotebook : n));
      }
    } catch (error) {
      console.error('Error:', error);
      // Add error message to chat
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, there was a network error. Please try again.',
        timestamp: new Date().toISOString()
      };

      const errorNotebook = {
        ...updatedNotebook,
        messages: [...updatedMessages, errorMessage],
        updatedAt: new Date().toISOString()
      };

      setActiveNotebook(errorNotebook);
      updateNotebooks(prev => prev.map(n => n.id === notebook.id ? errorNotebook : n));
    } finally {
      setIsLoading(false);
    }
  };

  const deleteNotebook = async (notebookId, e) => {
    e.stopPropagation();
    updateNotebooks(prev => prev.filter(n => n.id !== notebookId));
    if (activeNotebook?.id === notebookId) {
      setActiveNotebook(null);
    }

    try {
      await authPost('/api/notebooks/delete', { notebookId, mode: dataMode });
    } catch (error) {
      console.error('Error deleting notebook:', error);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="strategy-view">
      <div className="strategy-layout">
        {/* Sidebar toggle button */}
        <button
          className={`sidebar-toggle ${sidebarOpen ? 'open' : ''}`}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        {/* Sidebar with notebooks */}
        <div
          className={`notebooks-sidebar ${sidebarOpen ? 'open' : ''}`}
          onMouseLeave={() => setSidebarOpen(false)}
        >
          <button className="new-notebook-btn" onClick={createNewNotebook}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Strategy
          </button>

          <div className="notebooks-list">
            {notebooks.map(notebook => (
              <button
                key={notebook.id}
                className={`notebook-item ${activeNotebook?.id === notebook.id ? 'active' : ''}`}
                onClick={() => setActiveNotebook(notebook)}
              >
                <div className="notebook-item-content">
                  <span className="notebook-title">{notebook.title}</span>
                  <span className="notebook-date">{formatDate(notebook.updatedAt)}</span>
                </div>
                <button
                  className="notebook-delete"
                  onClick={(e) => deleteNotebook(notebook.id, e)}
                  aria-label="Delete notebook"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </button>
            ))}
          </div>
        </div>

        {/* Main chat area */}
        <div className="strategy-main">
          {activeNotebook ? (
            <>
              <div className="strategy-messages">
                {activeNotebook.messages.length === 0 ? (
                  <div className="strategy-empty-chat">
                    <StrategyEmptyState />
                    <p className="empty-chat-text">Start brainstorming your fundraising strategy</p>
                  </div>
                ) : (
                  activeNotebook.messages.map((message, index) => (
                    <div key={index} className={`strategy-message ${message.role}`}>
                      <div className="message-header">
                        <span className="message-role">
                          {message.role === 'user' ? 'You' : 'knowroom'}
                        </span>
                        <span className="message-time">{formatTime(message.timestamp)}</span>
                      </div>
                      <div className="message-content">
                        {message.content.split('\n\n').map((paragraph, pIndex) => (
                          <p key={pIndex}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  ))
                )}
                {isLoading && (
                  <div className="strategy-message assistant loading">
                    <div className="message-header">
                      <span className="message-role">knowroom</span>
                    </div>
                    <div className="typing-indicator">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="strategy-input-container">
                <SearchBox
                  placeholder="Continue the conversation..."
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onSearch={handleSearch}
                  expandable={true}
                  loading={isLoading}
                  autoFocus={true}
                />
              </div>
            </>
          ) : (
            <div className="strategy-no-notebook">
              <StrategyEmptyState />
              <h2>Strategy Notebook</h2>
              <p>Create a new strategy to start brainstorming</p>
              <button className="create-first-btn" onClick={createNewNotebook}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                New Strategy
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StrategyEmptyState() {
  return (
    <svg className="empty-state-icon" width="80" height="80" viewBox="0 0 120 120" fill="none">
      {/* Lightbulb / idea icon */}
      <ellipse cx="60" cy="45" rx="25" ry="28" stroke="currentColor" strokeWidth="2" className="bulb-outline" />
      <path d="M48 73 L48 85 Q48 90 53 90 L67 90 Q72 90 72 85 L72 73" stroke="currentColor" strokeWidth="2" className="bulb-base" />
      <line x1="52" y1="80" x2="68" y2="80" stroke="currentColor" strokeWidth="2" />
      <line x1="52" y1="85" x2="68" y2="85" stroke="currentColor" strokeWidth="2" />
      {/* Light rays */}
      <line x1="60" y1="10" x2="60" y2="5" stroke="currentColor" strokeWidth="2" className="ray" />
      <line x1="85" y1="20" x2="90" y2="15" stroke="currentColor" strokeWidth="2" className="ray" />
      <line x1="95" y1="45" x2="100" y2="45" stroke="currentColor" strokeWidth="2" className="ray" />
      <line x1="35" y1="20" x2="30" y2="15" stroke="currentColor" strokeWidth="2" className="ray" />
      <line x1="25" y1="45" x2="20" y2="45" stroke="currentColor" strokeWidth="2" className="ray" />
      {/* Inner glow */}
      <ellipse cx="60" cy="42" rx="12" ry="14" fill="currentColor" opacity="0.1" className="inner-glow" />
    </svg>
  );
}

export default StrategyView;
